<template>
  <v-card id="account-profile-personal" class="pl-8 pr-8 pt-8 pb-6 mt-4 mb-5 elevation-0">
    <BarLoading v-if="loading" class="mt-0 pt-0 pb-4" />

    <div class="items-flex">
      <UserAvatar size="60" />
      <div class="ml-5 mt-4">
        <div class="items-flex">
          <h2 class="mr-3">{{ $t('views.myAccount.email') }}</h2>
          <div class="content">
            {{ firebaseUser.email }}
          </div>
        </div>
        <div class="mt-4">
          <v-btn small outlined color="primary" @click="doLogout">
            <v-icon>mdi-logout</v-icon>
            <span class="ml-2">{{ $t('common.logout') }}</span>
          </v-btn>
        </div>
      </div>
    </div>

    <div v-if="!firebaseUser.emailVerified" class="mt-8 alert-verify">
      <v-alert
        border="left"
        type="warning"
        :color="$vuetify.theme.dark ? 'dark_Textgrey' : 'darkTextGrey'"
        outlined
        class="ma-0 pa-2"
      >
        <span>
          {{ $t('views.myAccount.emailNotVerified') }}
        </span>
        <span>
          <a class="ml-2" @click="resendVerificationEmail()">
            {{ $t('views.myAccount.resendVerificationEmail.title') }}
          </a>
        </span>
      </v-alert>
    </div>

    <v-divider class="mb-4 mt-8 account-profile-divider" />

    <div class="items-flex pt-5">
      <h2 class="mr-3">{{ $t('common.country') }}</h2>
      <div class="content">
        {{ $t(`common.countries.${myUser.country}`) }}
      </div>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-3">{{ $t('views.myAccount.baseCurrency') }}</h2>
      <div class="content">
        {{ myUser.baseCurrency }}
      </div>
    </div>

    <template v-if="!isProvider">
      <div class="pt-5">
        <div v-if="!changeName" class="items-flex align-end">
          <h2 class="mr-3">{{ $t('views.myAccount.name') }}</h2>
          <div class="content">
            {{ myUser.name }}
          </div>

          <a class="ml-3" @click="changeName = !changeName">
            {{ $t('common.edit') }}
          </a>
        </div>
        <div v-else class="items-flex align-end">
          <v-text-field
            v-model="myUser.name"
            :disabled="loading"
            :label="$t('views.myAccount.name')"
            class="small-input"
            hide-details
          />
          <a class="ml-3" @click="updateUser">
            {{ $t('views.myAccount.update') }}
          </a>
        </div>
      </div>
    </template>

    <v-divider class="mb-4 mt-15 account-profile-divider" />

    <div v-if="showReferralCode" class="referral-code mt-10 align-end">
      <a @click="changeReferralCode = !changeReferralCode">
        {{
          !changeReferralCode
            ? $t('views.myAccount.referralCode.show')
            : $t('views.myAccount.referralCode.hide')
        }}
      </a>
      <div v-if="changeReferralCode" class="items-flex mt-1">
        <v-text-field
          v-model="referralCode"
          :placeholder="$t('views.myAccount.referralCode.title')"
          name="referralCode"
          type="text"
          :disabled="loading"
          class="pa-0 ma-0 small-input"
          :error-messages="referralCodeError ? $t(`apiErrors.users.invalidReferralCode`) : null"
          :hide-details="!referralCodeError"
        />
        <a class="ml-3" @click="saveReferralCode">
          {{ $t('views.myAccount.referralCode.save') }}
        </a>
      </div>
    </div>

    <!-- <div class="small-input pt-5">
      <BirthdayPicker v-model="myUser.birthday" />
    </div> -->

    <div class="mt-12">
      <AccountProfileMore :loading.sync="loading" />
    </div>
  </v-card>
</template>

<script>
import firebase from 'firebase/app'
import gtag from '@/lib/gtag'
import { mapState, mapActions } from 'vuex'
import { pick, get } from 'lodash'
import { updateUser, addReferralCode } from '@/lib/api.js'
import { userAcceptedCountries } from '@/lib/countries/countries.js'
import UserAvatar from '@/views/components/core/UserAvatar'
import CountrySelector from '@/views/components/common/CountrySelector'
import CurrencySelector from '@/views/components/common/CurrencySelector'
import BirthdayPicker from '@/views/components/core/BirthdayPicker'
import AccountProfileMore from '@/views/components/account/AccountProfileMore'
import BarLoading from '@/views/components/core/BarLoading.vue'

export default {
  components: {
    UserAvatar,
    CountrySelector,
    CurrencySelector,
    BirthdayPicker,
    AccountProfileMore,
    BarLoading,
  },
  data: () => {
    return {
      loading: false,
      myUser: null,
      userAcceptedCountries,
      changeReferralCode: false,
      changeName: false,
      referralCode: null,
      referralCodeError: null,
    }
  },
  computed: {
    ...mapState(['user', 'firebaseUser', 'language']),
    smAndDown() {
      return this.$vuetify.breakpoint.smAndDown
    },
    isProvider() {
      return this.firebaseUser.providerData[0].providerId !== 'password'
    },
    showReferralCode() {
      return (
        !this.myUser.referralCode &&
        (this.user.status === 'TRIAL' || this.user.status === 'NO_PLAN')
      )
    },
  },
  watch: {
    user: {
      handler() {
        this.myUser = JSON.parse(JSON.stringify(this.user))
      },
      deep: true,
      immediate: true,
    },
    referralCode: {
      handler() {
        if (this.referralCode) this.$set(this, 'referralCode', this.referralCode.toUpperCase())
      },
    },
  },
  methods: {
    ...mapActions({ logout: 'LOGOUT', updateUsersInfo: 'UPDATE_USERS_INFO' }),
    doLogout() {
      gtag.pageview({
        pagePath: `/logout`,
        pageTitle: `logout`,
        pageContentGroup: 'modal',
      })

      this.logout()
    },
    async resendVerificationEmail() {
      this.loading = true
      try {
        firebase.auth().languageCode = this.language
        await firebase.auth().currentUser.sendEmailVerification()
        this.$root.$snackbar.success({
          text: this.$t('views.myAccount.resendVerificationEmail.ok'),
          timeout: 6000,
        })
      } catch (error) {
        this.$root.$snackbar.error({
          text: this.$t('views.myAccount.resendVerificationEmail.fail'),
        })
      }
      this.loading = false
    },
    async updateUser() {
      // const attributes = ['birthday']
      const attributes = []
      if (!this.isProvider) attributes.push('name')

      this.loading = true
      try {
        const data = pick(this.myUser, attributes)
        await updateUser({ uid: this.user.uid, data })

        await this.updateUsersInfo()

        this.changeName = false

        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        this.loading = false
      }
      this.loading = false
    },
    async saveReferralCode() {
      this.loading = true
      try {
        await addReferralCode({ uid: this.user.uid, referralCode: this.referralCode })
        await this.updateUsersInfo()
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
      } catch (error) {
        const errorCode = get(error, 'data.name', null)
        if (errorCode === 'users.invalidReferralCode') {
          this.referralCodeError = true
        } else {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        }
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss">
#account-profile-personal {
  font-size: 15px;
  .items-flex {
    display: flex;
    align-items: center;
    &.align-center {
      align-items: flex-end;
    }
  }
  h2 {
    font-size: 15px;
    font-weight: 400;
    color: #636363;
  }
  .content {
    font-weight: 500;
  }
  &.theme--dark {
    h2 {
      color: #a5a5a5;
    }
  }
  .small-input {
    max-width: 200px;
  }
  .alert-verify {
    max-width: 500px;
    span {
      font-size: 14px;
      font-weight: 300;
    }
  }
  .button-save-profile {
    color: #1f1f1f;
    &.theme--dark {
      color: white;
    }
  }
  .account-profile-divider {
    max-width: 400px;
    &.theme--dark {
      border-color: #292929;
    }
  }
  .referral-code {
    font-size: 13px;
    h2 {
      font-size: 13px;
    }
  }
}
</style>
